/**
 * Save a server-generated ZIP response without holding the whole archive
 * in the browser heap.
 *
 * `fetch → res.blob() → <a download>` was fine for the JSZip BIM bundles
 * it was written for (≤300 MB) but the same button fronts the streaming
 * As-Built / package ZIPs, where a multi-GB RVT bundle gets iOS Safari
 * killed and stalls desktop tabs.
 *
 * Strategy:
 *   1. Chromium desktop — `showSaveFilePicker` FIRST (it needs the click's
 *      transient activation, which a 30 s "Preparing ZIP" fetch outlives),
 *      then pipe the response body straight to disk.
 *   2. Otherwise buffer to a Blob while the body stays under
 *      `ZIP_BLOB_MAX_BYTES`; the moment it would exceed that, cancel the
 *      body and hand the URL to the browser download manager via an
 *      anchor (the route sets `Content-Disposition: attachment`, so no
 *      navigation happens). Second request, but no heap.
 */

import { ChunkCoalescer } from "@/lib/virtualspace/chunk-coalescer";

export const ZIP_BLOB_MAX_BYTES = 400 * 1024 * 1024;

export type ZipSaveStrategy = "stream-to-disk" | "blob" | "anchor";

export type ZipSaveProgress = {
  phase: "preparing" | "saving";
  bytesSaved: number;
  bytesTotal: number | null;
};

type SaveFilePickerWindow = Window & {
  showSaveFilePicker?: (opts?: {
    suggestedName?: string;
    types?: Array<{ description?: string; accept: Record<string, string[]> }>;
  }) => Promise<FileSystemFileHandle>;
};

export function canStreamZipToDisk(win: Window | undefined = globalThis.window): boolean {
  return typeof (win as SaveFilePickerWindow | undefined)?.showSaveFilePicker === "function";
}

/** Exported for tests — pick the non-FSA path from what the response declares. */
export function chooseBufferedZipStrategy(
  contentLength: number | null,
  maxBlobBytes = ZIP_BLOB_MAX_BYTES,
): Exclude<ZipSaveStrategy, "stream-to-disk"> {
  if (contentLength != null && contentLength > maxBlobBytes) return "anchor";
  return "blob";
}

export function parseContentLength(value: string | null): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function zipFileNameFromDisposition(
  disposition: string | null,
  fallback: string,
): string {
  if (!disposition) return fallback;
  const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // fall through to the plain form
    }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain?.[1]?.trim() || fallback;
}

export function triggerAnchorDownload(href: string, fileName: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function readZipError(res: Response): Promise<string> {
  let msg = `ZIP request failed (${res.status})`;
  try {
    const data = (await res.json()) as { error?: unknown };
    if (data?.error) msg = String(data.error);
  } catch {
    // Body wasn't JSON — fall back to status.
  }
  return msg;
}

/**
 * The ZIP routes stream from a serverless function that is capped at five
 * minutes, so a slow or very large package is cut off mid-archive and the
 * browser only reports "network error" / "Failed to fetch".
 */
function interruptedZipError(bytesSaved: number, cause: Error): Error {
  const mb = Math.round(bytesSaved / (1024 * 1024));
  return new Error(
    `The ZIP stream was cut off after ${mb} MB (${cause.message}). ` +
      "Very large packages can exceed the server's 5-minute limit for one ZIP — " +
      "the partial file was discarded. Try again, or download the files individually.",
    { cause },
  );
}

export type SaveZipArgs = {
  url: string;
  /** Used for the save dialog and as the fallback filename. */
  fileName: string;
  onProgress?: (p: ZipSaveProgress) => void;
  /** Tests inject these. */
  fetchImpl?: typeof fetch;
  win?: Window;
  maxBlobBytes?: number;
};

export type SaveZipResult =
  | { outcome: "saved"; strategy: ZipSaveStrategy; bytes: number | null }
  | { outcome: "cancelled" };

/**
 * Save the ZIP at `url`. Throws with the server's error text when the
 * route refuses (409 "still preparing", 413 too many files, …).
 */
export async function saveZipResponse(args: SaveZipArgs): Promise<SaveZipResult> {
  const fetchImpl = args.fetchImpl ?? fetch;
  const win = (args.win ?? globalThis.window) as SaveFilePickerWindow;
  const maxBlobBytes = args.maxBlobBytes ?? ZIP_BLOB_MAX_BYTES;

  if (typeof win.showSaveFilePicker === "function") {
    let handle: FileSystemFileHandle;
    try {
      handle = await win.showSaveFilePicker({
        suggestedName: args.fileName,
        types: [{ description: "ZIP archive", accept: { "application/zip": [".zip"] } }],
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return { outcome: "cancelled" };
      }
      throw err;
    }
    args.onProgress?.({ phase: "preparing", bytesSaved: 0, bytesTotal: null });
    const res = await fetchImpl(args.url, { cache: "no-store" });
    if (!res.ok || !res.body) {
      // Chromium already created the picked file. Remove the 0-byte
      // placeholder where the (Chromium-only) API exists, so the user
      // is not left with a .zip that Archive Utility calls unsupported.
      const removable = handle as FileSystemFileHandle & {
        remove?: () => Promise<void>;
      };
      if (typeof removable.remove === "function") {
        await removable.remove().catch(() => undefined);
      }
      throw new Error(await readZipError(res));
    }
    const removePlaceholder = async () => {
      const removable = handle as FileSystemFileHandle & {
        remove?: () => Promise<void>;
      };
      if (typeof removable.remove === "function") {
        await removable.remove().catch(() => undefined);
      }
    };
    let writable: FileSystemWritableFileStream;
    try {
      writable = await handle.createWritable();
    } catch (err) {
      await removePlaceholder();
      throw err;
    }
    const total = parseContentLength(res.headers.get("content-length"));
    let saved = 0;
    args.onProgress?.({ phase: "saving", bytesSaved: 0, bytesTotal: total });
    const reader = res.body.getReader();
    const coalescer = new ChunkCoalescer();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) {
          saved += value.byteLength;
          const batch = coalescer.push(value);
          if (batch) await writable.write(batch);
          args.onProgress?.({ phase: "saving", bytesSaved: saved, bytesTotal: total });
        }
      }
      const rest = coalescer.drain();
      if (rest) await writable.write(rest);
      if (total != null && saved < total) {
        throw new Error(
          `ZIP download ended early (${saved} of ${total} bytes). Try again.`,
        );
      }
      await writable.close();
      if (saved > 0 && typeof handle.getFile === "function") {
        const onDisk = (await handle.getFile()).size;
        if (onDisk < saved) {
          throw new Error(
            `The ZIP downloaded but only ${onDisk} of ${saved} bytes reached disk — the browser's temporary file was lost (cloud-sync folder or antivirus?).`,
          );
        }
      }
    } catch (err) {
      await writable.abort().catch(() => undefined);
      await reader.cancel().catch(() => undefined);
      // Chromium only moves its temporary file into place on close(), so a
      // failed save otherwise leaves the picked file at 0 KB.
      await removePlaceholder();
      throw err instanceof TypeError ? interruptedZipError(saved, err) : err;
    }
    return { outcome: "saved", strategy: "stream-to-disk", bytes: saved };
  }

  args.onProgress?.({ phase: "preparing", bytesSaved: 0, bytesTotal: null });
  const res = await fetchImpl(args.url, { cache: "no-store" });
  if (!res.ok) throw new Error(await readZipError(res));
  const total = parseContentLength(res.headers.get("content-length"));
  const finalName = zipFileNameFromDisposition(
    res.headers.get("content-disposition"),
    args.fileName,
  );

  if (chooseBufferedZipStrategy(total, maxBlobBytes) === "anchor" || !res.body) {
    await res.body?.cancel().catch(() => undefined);
    triggerAnchorDownload(args.url, finalName);
    return { outcome: "saved", strategy: "anchor", bytes: total };
  }

  args.onProgress?.({ phase: "saving", bytesSaved: 0, bytesTotal: total });
  const chunks: Uint8Array[] = [];
  let saved = 0;
  const reader = res.body.getReader();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    if (!value) continue;
    saved += value.byteLength;
    if (saved > maxBlobBytes) {
      // Streaming route with no Content-Length that turned out huge:
      // stop hoarding heap and let the download manager take over.
      await reader.cancel().catch(() => undefined);
      chunks.length = 0;
      triggerAnchorDownload(args.url, finalName);
      return { outcome: "saved", strategy: "anchor", bytes: null };
    }
    chunks.push(value);
    args.onProgress?.({ phase: "saving", bytesSaved: saved, bytesTotal: total });
  }
  const blob = new Blob(chunks as BlobPart[], { type: "application/zip" });
  const objectUrl = URL.createObjectURL(blob);
  triggerAnchorDownload(objectUrl, finalName);
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  return { outcome: "saved", strategy: "blob", bytes: saved };
}
